import type {
  AccessMode,
  AdminBadges,
  AdminFeedback,
  AdminFeedbackUpdate,
  AdminJob,
  AdminOverview,
  AdminSettings,
  AdminSettingsUpdate,
  AdminTenant,
  AdminTenantUpdate,
  AdminTurnRow,
  AnalyzeRequest,
  AuthConfig,
  Box31Entry,
  Box31Request,
  ByokRequest,
  DeclaredCodeRequest,
  DeviceLoginView,
  FeedbackRequest,
  FeedbackSummary,
  Language,
  Me,
  ModelAccessView,
  PreferredAccessRequest,
  RenameSessionRequest,
  ReuseRequest,
  SendMessageRequest,
  SendMessageResponse,
  SessionDetail,
  SessionSummary,
  StartJobRequest,
  StoredDocument,
  TariffLineView,
  Turn,
  TurnStatus,
  TurnTrace,
  UktzedNode,
  UktzedNodeDetail,
  UktzedSearchResult,
  UpdateMeRequest,
  UsageView,
} from "../../../shared/api.ts";
import { request } from "./http.ts";

/** `GET /api/admin/turns` filters. */
export type AdminTurnFilters = { status: TurnStatus | ""; tenantId: string; limit: number };

export type { AuthConfig, RenameSessionRequest, StoredDocument };

export type FeedbackStatusFilter = AdminFeedback["status"] | "all";

const enc = encodeURIComponent;

export const api = {
  me: () => request<Me>("GET", "/api/me", undefined, { allowUnauthorized: true }),
  updateMe: (body: UpdateMeRequest) => request<Me>("PATCH", "/api/me", body),
  authConfig: () => request<AuthConfig>("GET", "/api/auth/config", undefined, { allowUnauthorized: true }),
  logout: () => request<void>("POST", "/auth/logout", undefined, { allowUnauthorized: true }),

  sessions: () => request<SessionSummary[]>("GET", "/api/sessions"),
  createSession: () => request<SessionSummary>("POST", "/api/sessions", {}),
  session: (id: string) => request<SessionDetail>("GET", `/api/sessions/${enc(id)}`),
  renameSession: (id: string, body: RenameSessionRequest) => request<SessionSummary>("PATCH", `/api/sessions/${enc(id)}`, body),
  deleteSession: (id: string) => request<void>("DELETE", `/api/sessions/${enc(id)}`),
  sendMessage: (id: string, body: SendMessageRequest) =>
    request<SendMessageResponse>("POST", `/api/sessions/${enc(id)}/messages`, body),
  analyze: (id: string, body: AnalyzeRequest) => request<Turn | SessionDetail | null>("POST", `/api/sessions/${enc(id)}/analyze`, body),
  reuse: (id: string, body: ReuseRequest) => request<SessionDetail>("POST", `/api/sessions/${enc(id)}/reuse`, body),
  setDeclaredCode: (id: string, body: DeclaredCodeRequest) =>
    request<unknown>("PUT", `/api/sessions/${enc(id)}/declared-code`, body),
  sendFeedback: (id: string, body: FeedbackRequest) =>
    request<FeedbackSummary | null>("POST", `/api/sessions/${enc(id)}/feedback`, body),
  exportPdfUrl: (id: string, language: Language) => `/api/sessions/${enc(id)}/export.pdf?lang=${language}`,
  startBox31: (id: string, body: Box31Request) => request<Box31Entry>("POST", `/api/sessions/${enc(id)}/box31`, body),

  retryTurn: (id: string) => request<Turn | null>("POST", `/api/turns/${enc(id)}/retry`),
  cancelTurn: (id: string) => request<Turn | null>("POST", `/api/turns/${enc(id)}/cancel`),
  turnEventsUrl: (id: string) => `/api/turns/${enc(id)}/events`,

  tariffLookup: (query: string, signal?: AbortSignal) =>
    request<TariffLineView[]>("GET", `/api/tariff/lookup?q=${enc(query)}`, undefined, { signal }),
  document: (id: string) => request<StoredDocument>("GET", `/api/documents/${enc(id)}`),

  uktzedChildren: (parent: string | null) =>
    request<UktzedNode[]>("GET", parent ? `/api/uktzed/children?parent=${enc(parent)}` : "/api/uktzed/children"),
  uktzedNode: (id: string) => request<UktzedNodeDetail>("GET", `/api/uktzed/nodes/${enc(id)}`),
  uktzedSearch: (query: string, signal?: AbortSignal) =>
    request<UktzedSearchResult>("GET", `/api/uktzed/search?q=${enc(query)}`, undefined, { signal }),

  modelAccess: () => request<ModelAccessView>("GET", "/api/settings/model-access"),
  requestModelAccess: () => request<ModelAccessView>("POST", "/api/settings/model-access/request", {}),
  startSubscriptionLogin: () => request<DeviceLoginView>("POST", "/api/settings/model-access/subscription/start", {}),
  deviceLogin: (id: string) => request<DeviceLoginView>("GET", `/api/settings/model-access/subscription/login/${enc(id)}`),
  cancelDeviceLogin: (id: string) =>
    request<DeviceLoginView | null>("POST", `/api/settings/model-access/subscription/login/${enc(id)}/cancel`, {}),
  disconnectSubscription: () => request<unknown>("DELETE", "/api/settings/model-access/subscription"),
  saveByok: (body: ByokRequest) => request<unknown>("PUT", "/api/settings/model-access/byok", body),
  removeByok: () => request<unknown>("DELETE", "/api/settings/model-access/byok"),
  setPreferred: (body: PreferredAccessRequest) => request<unknown>("PUT", "/api/settings/model-access/preferred", body),

  usage: (month: string) => request<UsageView>("GET", `/api/usage?month=${enc(month)}`),

  admin: {
    overview: () => request<AdminOverview>("GET", "/api/admin/overview"),
    jobs: () => request<AdminJob[]>("GET", "/api/admin/jobs"),
    startJob: (body: StartJobRequest) => request<AdminJob>("POST", "/api/admin/jobs", body),
    tenants: () => request<AdminTenant[]>("GET", "/api/admin/tenants"),
    badges: () => request<AdminBadges>("GET", "/api/admin/badges"),
    updateTenant: (id: string, body: AdminTenantUpdate) => request<AdminTenant>("PATCH", `/api/admin/tenants/${enc(id)}`, body),
    turns: (filters: AdminTurnFilters) => {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.tenantId) params.set("tenantId", filters.tenantId);
      params.set("limit", String(filters.limit));
      return request<AdminTurnRow[]>("GET", `/api/admin/turns?${params.toString()}`);
    },
    trace: (id: string) => request<TurnTrace>("GET", `/api/admin/turns/${enc(id)}/trace`),
    feedback: (status: FeedbackStatusFilter) =>
      request<AdminFeedback[]>("GET", status === "all" ? "/api/admin/feedback" : `/api/admin/feedback?status=${status}`),
    updateFeedback: (id: string, body: AdminFeedbackUpdate) =>
      request<AdminFeedback>("PATCH", `/api/admin/feedback/${enc(id)}`, body),
    session: (id: string) => request<SessionDetail>("GET", `/api/admin/sessions/${enc(id)}`),
    settings: () => request<AdminSettings>("GET", "/api/admin/settings"),
    saveSettings: (body: AdminSettingsUpdate) => request<AdminSettings>("PUT", "/api/admin/settings", body),
  },
};

/** TanStack Query keys, as listed in the UI spec. */
export const queryKeys = {
  me: ["me"] as const,
  authConfig: ["auth-config"] as const,
  sessions: ["sessions"] as const,
  session: (id: string) => ["session", id] as const,
  modelAccess: ["model-access"] as const,
  deviceLogin: (id: string) => ["model-access", "login", id] as const,
  usage: (month: string) => ["usage", month] as const,
  tariff: (query: string) => ["tariff", query] as const,
  document: (id: string) => ["document", id] as const,
  uktzed: {
    children: (parent: string | null) => ["uktzed", "children", parent ?? ""] as const,
    node: (id: string) => ["uktzed", "node", id] as const,
    search: (query: string) => ["uktzed", "search", query] as const,
  },
  admin: {
    all: ["admin"] as const,
    overview: ["admin", "overview"] as const,
    jobs: ["admin", "jobs"] as const,
    tenants: ["admin", "tenants"] as const,
    badges: ["admin", "badges"] as const,
    turns: (filters: AdminTurnFilters) => ["admin", "turns", filters] as const,
    trace: (id: string) => ["admin", "trace", id] as const,
    feedback: (status: FeedbackStatusFilter) => ["admin", "feedback", status] as const,
    session: (id: string) => ["admin", "session", id] as const,
    settings: ["admin", "settings"] as const,
  },
};

export const ACCESS_MODES: AccessMode[] = ["subscription", "byok", "platform", "dev"];
